import React, { FC, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { LABEL_PLACEMENT } from 'baseui/checkbox';
import { Trans, useTranslation } from 'react-i18next';
import SubmitButtonView from './SubmitButtonView';
import SectionSeparator from './styled-components/SectionSeparator';

import store, { useAppSelector } from 'store';
import { useTrackGoogleEvents } from '../../../../../interface_adapters/hooks/GoogleTagContext';
import { usePreferredSubmitIntention } from '../../../../../interface_adapters/hooks/usePreferredSubmitIntention';
import useSummaryPageContext from '../../../../../frameworks_drivers/context/SummaryPageContext';
import { useNavigate } from 'react-router-dom';
import { SetCommonConsentsToOnboardingStoreUseCase } from '../../../../../application/use_cases/consent/SetCommonConsentsToOnboardingStoreUseCase';
import { paths } from '../../../../../frameworks_drivers/routes';
import { TrackerUtil } from '../../../../../tracking/TrackerUtil';
import { createNewLead, createNewOrder } from '../../../../../application/ports/TransactionService';
import { getABTestingObject } from '../../../../../frameworks_drivers/redux/reducers/abTest/abTesting';
import Checkbox from '../../../../components/Checkbox';
import AgbInlineLink from './AgbInlineLink';
import DataProcessingAgreementInlineLink from './DataProcessingAgreementInlineLink';
import { DataProcessingAgreement } from '../../../../../domain/DataProcessingAgreement';
import { ReduxConsentStoreService } from '../../../../../interface_adapters/services/store/ReduxConsentStoreService';
import { AddDataProcessingAgreementToOnboardingStoreUseCase } from '../../../../../application/use_cases/consent/AddDataProcessingAgreementToOnboardingStoreUseCase';

const SubmitView: FC = () => {
  const [isTermsOfServiceAccepted, setIsTermsOfServiceAccepted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { trackSubmitOrder, trackSubmitOffer } = useTrackGoogleEvents();
  const { preferredSubmitIntention } = usePreferredSubmitIntention();
  const context = useSummaryPageContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dataProcessingAgreement: DataProcessingAgreement | null = useAppSelector(
    (state) => state.misc.dataProcessingAgreement
  );

  const setConsentUseCase = new SetCommonConsentsToOnboardingStoreUseCase();

  const consentStore = useMemo(() => {
    return new ReduxConsentStoreService();
  }, []);

  useEffect(() => {
    consentStore.resetGivenConsents();
  }, [consentStore]);

  const handleOpenAgbInModal = (): void => {
    context.setIsTermsOfServiceModalOpen(true);
  };

  const handleOpenAgbInNewWindow = (): void => {
    window.open(`/onboarding#${paths.TOS}`, '_blank');
  };

  // TODO: Define correct path for DPA
  const handleOpenDataProcessingAgreementInNewWindow = (): void => {
    window.open(dataProcessingAgreement?.url, '_blank', 'noopener,noreferrer');
  };

  const handleTOSAcceptCheckboxChange = (checked: boolean): void => {
    TrackerUtil.trackCheckboxClick(
      `${TrackerUtil.getPageName()}_checkbox_terms-and-conditions`,
      'checkbox_terms-and-conditions'
    );
    // User clicked on link to open modal, so don't tick the box.
    if (context.isTermsOfServiceModalOpen) return;

    setIsTermsOfServiceAccepted(checked);
  };

  const resetCheckbox = () => setIsTermsOfServiceAccepted(false);
  const navigateToNextPage = () => navigate(paths.ConfirmEmail);

  const handleSendViaEMailButtonClicked = () => {
    TrackerUtil.trackBtnClickByIdentifier('request-offer-by-mail');
    setIsLoading(true);

    setConsentUseCase
      .execute(t)
      .then(() => {
        createNewLead(store.getState().onboardingInfo, getABTestingObject(), store.getState().misc)
          .then((response: any) => {
            setIsLoading(false);
            navigateToNextPage();
          })
          .catch((error: any) => {
            TrackerUtil.trackError('api-offer-server-error', error.message);
            setIsLoading(false);
            context.setApiErrors(error);
          });

        trackSubmitOffer();
      })
      .catch((error: any) => {
        console.error('Error loading document versions!');
        setIsLoading(false);
        context.setApiErrors(error);
      });
  };

  const handleOrderNowButtonClicked = () => {
    TrackerUtil.trackBtnClickByIdentifier('order-now');
    setIsLoading(true);

    const addDpaToOnboardingStore = new AddDataProcessingAgreementToOnboardingStoreUseCase(
      consentStore
    );

    if (dataProcessingAgreement) addDpaToOnboardingStore.execute(dataProcessingAgreement?.version);

    setConsentUseCase
      .execute(t, true)
      .then(() => {
        createNewOrder(store.getState().onboardingInfo, getABTestingObject(), store.getState().misc)
          .then((response: any) => {
            setIsLoading(false);
            navigateToNextPage();
          })
          .catch((error: any) => {
            TrackerUtil.trackError('api-order-now-server-error', error.message);
            setIsLoading(false);
            context.setApiErrors(error);
          });

        trackSubmitOrder();
      })
      .catch((error: any) => {
        console.error('Error loading document versions!');
        setIsLoading(false);
        context.setApiErrors(error);
      });
  };

  return (
    <>
      <Row>
        <Col xs>
          <Checkbox
            checked={isTermsOfServiceAccepted}
            onChange={(e) => handleTOSAcceptCheckboxChange(e.currentTarget.checked)}
            error={false}
            labelPlacement={LABEL_PLACEMENT.right}
            additionalTestId="accept-agb"
          >
            {preferredSubmitIntention === 'order' && (
              <Trans
                i18nKey={'pageSummaryFinalSectionTOSAndDPACheckboxLabel'}
                tOptions={{ skipPostProcessing: true }}
              >
                <AgbInlineLink
                  linkText={t('pageSummaryFinalSectionTOS')}
                  handleOpenAgbInModal={handleOpenAgbInModal}
                  handleOpenAgbViaWindow={handleOpenAgbInNewWindow}
                />
                <DataProcessingAgreementInlineLink
                  linkText={t('pageSummaryFinalSectionDataProcessingAgreement')}
                  handleOpenDpaViaWindow={handleOpenDataProcessingAgreementInNewWindow}
                />
              </Trans>
            )}

            {preferredSubmitIntention === 'offer' && (
              <Trans
                i18nKey={'pageSummaryFinalSectionTOSCheckboxLabel'}
                tOptions={{ skipPostProcessing: true }}
              >
                <AgbInlineLink
                  linkText={t('pageSummaryFinalSectionTOS')}
                  handleOpenAgbInModal={handleOpenAgbInModal}
                  handleOpenAgbViaWindow={handleOpenAgbInNewWindow}
                />
              </Trans>
            )}
          </Checkbox>
        </Col>
      </Row>

      <SectionSeparator />

      <Row className="justify-content-center">
        <SubmitButtonView
          onPrimaryClick={() => handleOrderNowButtonClicked()}
          onSecondaryClick={() => handleSendViaEMailButtonClicked()}
          primaryText={t('pageSummaryButtonTextOrderNow')}
          secondaryText={t('pageSummaryButtonTextSendViaMail')}
          isPrimaryDisabled={!isTermsOfServiceAccepted || isLoading}
          isSecondaryDisabled={!isTermsOfServiceAccepted || isLoading}
          isLoading={isLoading}
          resetCheckbox={resetCheckbox}
        />
      </Row>
    </>
  );
};

export default SubmitView;
